import api from './api'
import { authApi } from './api'
import type { LoginResponse } from './api'
import type { PasskeyRegistrationResponse } from '@/types/investor'

// =============================================================================
// Types
// =============================================================================

interface PasskeyCredentialDescriptor {
  id: string
  type: 'public-key'
  transports?: AuthenticatorTransport[]
}


export interface RegistrationCredential {
  id: string
  rawId: string
  type: string
  response: {
    attestationObject: string
    clientDataJSON: string
  }
}

export interface RegistrationResult {
  credential: RegistrationCredential
  challenge: string
}

interface AuthenticationAssertion {
  id: string
  rawId: string
  type: string
  response: {
    authenticatorData: string
    clientDataJSON: string
    signature: string
    userHandle: string | null
  }
}

// =============================================================================
// Helpers
// =============================================================================

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = base64.length % 4
  const padded = pad ? base64 + '='.repeat(4 - pad) : base64
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// =============================================================================
// Public API
// =============================================================================

export function isPasskeySupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential
}

// =============================================================================
// Registration (two-step)
// =============================================================================

/**
 * Step 1: Get registration options from server and prompt user to create a
 * passkey via the browser WebAuthn API. Returns the credential data to be
 * sent to the server in step 2.
 */
export async function beginRegistration(): Promise<RegistrationResult> {
  const optionsRes = await authApi.getPasskeyRegistrationOptions()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawOptions = optionsRes.data.options as any
  const challenge = optionsRes.data.challenge

  // Convert base64url challenge to ArrayBuffer for WebAuthn API
  const publicKeyOptions: PublicKeyCredentialCreationOptions = {
    rp: rawOptions.rp,
    pubKeyCredParams: rawOptions.pubKeyCredParams,
    challenge: base64urlToBuffer(rawOptions.challenge),
    user: {
      ...rawOptions.user,
      id: base64urlToBuffer(rawOptions.user.id),
    },
    excludeCredentials: rawOptions.excludeCredentials?.map((cred: { id: string; type: string; transports?: string[] }) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    })),
    authenticatorSelection: rawOptions.authenticatorSelection,
    timeout: rawOptions.timeout,
    attestation: rawOptions.attestation,
  }

  const credential = await navigator.credentials.create({
    publicKey: publicKeyOptions,
  }) as PublicKeyCredential | null

  if (!credential) {
    throw new Error('PASSKEY_CANCELLED')
  }

  const response = credential.response as AuthenticatorAttestationResponse
  return {
    credential: {
      id: credential.id,
      rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
      type: credential.type,
      response: {
        attestationObject: btoa(
          String.fromCharCode(...new Uint8Array(response.attestationObject))
        ),
        clientDataJSON: btoa(
          String.fromCharCode(...new Uint8Array(response.clientDataJSON))
        ),
      },
    },
    challenge,
  }
}

/**
 * Step 2: Send the credential from beginRegistration() to the server for
 * verification, optionally with a device name.
 */
export async function completeRegistration(
  credential: RegistrationCredential,
  challenge: string,
  deviceName?: string
): Promise<PasskeyRegistrationResponse> {
  const { data } = await authApi.registerPasskey({
    credential,
    challenge,
    device_name: deviceName || undefined,
  })
  return data
}

// =============================================================================
// Authentication (two-step)
// =============================================================================

/**
 * Step 1: Get authentication options from server and prompt user for their
 * passkey via the browser WebAuthn API. Returns the assertion data to be
 * sent to the server in step 2.
 */
export async function beginAuthentication(): Promise<{ assertion: AuthenticationAssertion; challenge: string }> {
  const { data } = await api.post<{ options: Record<string, unknown>; challenge: string }>(
    '/api/auth/passkey/login/begin'
  )

  const challenge = data.challenge

  const rawOptions = data.options as {
    challenge: string
    timeout?: number
    rpId?: string
    allowCredentials?: PasskeyCredentialDescriptor[]
    userVerification?: UserVerificationRequirement
  }

  const publicKeyOptions: PublicKeyCredentialRequestOptions = {
    ...rawOptions,
    challenge: base64urlToBuffer(rawOptions.challenge),
    allowCredentials: rawOptions.allowCredentials?.map((cred) => ({
      id: base64urlToBuffer(cred.id),
      type: cred.type,
      transports: cred.transports,
    })),
  }

  const credential = await navigator.credentials.get({
    publicKey: publicKeyOptions,
  }) as PublicKeyCredential | null

  if (!credential) {
    throw new Error('PASSKEY_CANCELLED')
  }

  const response = credential.response as AuthenticatorAssertionResponse
  return {
    assertion: {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        authenticatorData: bufferToBase64url(response.authenticatorData),
        clientDataJSON: bufferToBase64url(response.clientDataJSON),
        signature: bufferToBase64url(response.signature),
        userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
      },
    },
    challenge,
  }
}

/**
 * Step 2: Send the assertion from beginAuthentication() to the server for
 * verification. Returns the login response with tokens.
 */
export async function completeAuthentication(
  assertion: AuthenticationAssertion,
  challenge: string,
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>(
    '/api/auth/passkey/login/complete',
    { credential: assertion, challenge }
  )
  return data
}

// =============================================================================
// Convenience wrappers (backward-compatible)
// =============================================================================

export async function authenticateWithPasskey(): Promise<LoginResponse> {
  const { assertion, challenge } = await beginAuthentication()
  return completeAuthentication(assertion, challenge)
}

export async function registerPasskey(deviceName?: string): Promise<PasskeyRegistrationResponse> {
  const { credential, challenge } = await beginRegistration()
  return completeRegistration(credential, challenge, deviceName)
}
